import posthog from "posthog-js";
import { getStoredFirstTouchAttribution } from "@/lib/attribution";
import { isMetaTrackablePath } from "@/lib/meta-safe-url";

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

/**
 * Events we want in PostHog (in addition to GA4) so funnels work in PostHog.
 * Curated to the gate / quiz / test / booking journeys — the surfaces where
 * conversion drop-off matters. Everything else stays GA4-only to keep PostHog
 * event volume sane at our visitor count (~1000/day).
 *
 * Match by event name OR by prefix.
 */
const POSTHOG_EVENTS = new Set([
  // Zenyra chat → gate funnel
  "sesiune_ai_started",
  "sesiune_ai_first_user_msg",
  "sesiune_ai_completed",
  "sesiune_ai_final_viewed",
  "sesiune_ai_gate_unlocked",
  "zenyra_error",
  "recommendation_gate_viewed",
  "recommendation_gate_method",
  "sign_up",
  "recommendation_gate_signin",
  "recommendation_gate_email_fallback",
  // /gaseste-terapeut quiz funnel
  "questionnaire_cta_click",
  "questionnaire_intro_complete",
  "questionnaire_step_complete",
  "questionnaire_submitted",
  "match_received",
  // /teste funnel
  "test_started",
  "test_completed",
  "test_email_submitted",
  // Therapist profile → booking funnel (bounce vs. demo request vs. guest
  // checkout vs. contact fallback) — added so PostHog can measure conversion
  // on /terapeuti/[slug], not just GA4.
  "view_therapist_profile",
  "demo_request_open",
  "demo_request_submit",
  "guest_checkout_start",
  "contact_request_open",
  "contact_request_submit",
  // Homepage CTAs (hero, how-it-works, therapist strip, beyond, needs), each
  // tagged with `location` — added to see where / visitors click before they
  // leave, next to the replays recorded there.
  "cta_click",
  "zenyra_cta_click",
  "zenyra_cta_resume",
  "needs_click",
]);
const POSTHOG_EVENT_PREFIXES = [
  "recommendation_gate_",
  "sesiune_ai_",
  // /sesiune-cunoastere paid LP — needed to compare Zenyra CTA placements
  "discovery_lp_",
];

function shouldDualFireToPostHog(action: string): boolean {
  if (POSTHOG_EVENTS.has(action)) return true;
  return POSTHOG_EVENT_PREFIXES.some((p) => action.startsWith(p));
}

const getAttributionParams = (): AnalyticsParams => {
  const attr = getStoredFirstTouchAttribution();
  if (!attr) return {};
  return {
    acquisition_type: attr.acquisition_type,
    utm_source: attr.utm_source ?? undefined,
    utm_medium: attr.utm_medium ?? undefined,
    utm_campaign: attr.utm_campaign ?? undefined,
    utm_content: attr.utm_content ?? undefined,
    utm_term: attr.utm_term ?? undefined,
    ...(attr.gclid ? { gclid: attr.gclid } : {}),
    ...(attr.fbclid ? { fbclid: attr.fbclid } : {}),
  };
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

const ensureGtagStub = () => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer?.push(args);
    };
  }
};

export const setGoogleConsentDefaults = () => {
  ensureGtagStub();
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
};

export const updateGoogleConsent = (options: {
  analytical?: boolean;
  marketing?: boolean;
}) => {
  ensureGtagStub();
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;

  const analyticsGranted = options.analytical === true;
  const marketingGranted = options.marketing === true;

  window.gtag("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: marketingGranted ? "granted" : "denied",
  });
};

export const trackPageView = (path?: string) => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;

  const pagePath =
    path ?? `${window.location.pathname}${window.location.search}`;

  window.gtag("event", "page_view", {
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title,
    ...toGa4Params(getAttributionParams()),
  });
};

// Meta hears only about the pages in lib/meta-safe-url.ts: it blocked the
// domain as health data after seeing condition pages in the pixel's URLs.
const metaAllowedHere = () => isMetaTrackablePath(window.location.pathname);

export const trackMetaPageView = () => {
  if (typeof window === "undefined") return;
  if (typeof window.fbq !== "function") return;
  if (!metaAllowedHere()) return;
  window.fbq("track", "PageView");
};

export const trackEvent = (
  action: string,
  params?: AnalyticsParams,
  opts?: {
    /** Called once GA4 has sent the hit (or gave up), for callers about to leave the page. */
    onSent?: () => void;
  },
) => {
  if (typeof window === "undefined") return;

  // Dual-fire curated funnel events to PostHog so funnels work there too.
  // Recording is route-gated to /zenyra only — these events fire everywhere
  // regardless, which is what powers PostHog funnels for /teste, /gaseste-terapeut,
  // and the Zenyra/gate path.
  if (shouldDualFireToPostHog(action)) {
    try {
      posthog.capture(action, { ...getAttributionParams(), ...(params ?? {}) });
    } catch {
      /* posthog not initialized */
    }
  }

  if (typeof window.gtag !== "function") {
    opts?.onSent?.();
    return;
  }
  window.gtag("event", action, {
    ...toGa4Params({ ...getAttributionParams(), ...(params ?? {}) }),
    ...(opts?.onSent ? { event_callback: opts.onSent, event_timeout: 1500 } : {}),
  });
};

/**
 * GA4 reads some event parameters as a manual traffic source: an event carrying
 * `source`, `medium`, `campaign` or `gclid` starts a new session attributed to
 * that value. ~34 call sites pass `source` as "where on the page the click came
 * from" (`teste_index`, `chat`, …), which GA4 filed under Unassigned, and the
 * first-touch `gclid` rode along on every event, re-crediting returning
 * visitors to an old ad click. Rename them on the GA4 copy only; PostHog keeps
 * the original keys so its funnels are untouched.
 */
const GA4_RESERVED_PARAMS: Record<string, string> = {
  source: "cta_source",
  medium: "cta_medium",
  campaign: "cta_campaign",
  campaign_id: "cta_campaign_id",
  term: "cta_term",
  content: "cta_content",
  gclid: "first_touch_gclid",
  dclid: "first_touch_dclid",
};

const toGa4Params = (params: AnalyticsParams): AnalyticsParams => {
  const out: AnalyticsParams = {};
  for (const [key, value] of Object.entries(params)) {
    out[GA4_RESERVED_PARAMS[key] ?? key] = value;
  }
  return out;
};

// Fix 4: sync first-touch attribution to GA4 user properties
export const syncAttributionToGA4 = () => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  const attr = getStoredFirstTouchAttribution();
  if (!attr) return;
  window.gtag("set", "user_properties", {
    acquisition_type: attr.acquisition_type,
    utm_source: attr.utm_source ?? undefined,
    utm_medium: attr.utm_medium ?? undefined,
    utm_campaign: attr.utm_campaign ?? undefined,
    landing_path: attr.landing_path,
  });
};

export const trackMetaEvent = (
  action: string,
  params?: Record<string, unknown>,
  eventId?: string,
) => {
  if (typeof window === "undefined") return;
  if (typeof window.fbq !== "function") return;
  if (!metaAllowedHere()) return;
  window.fbq("trackCustom", action, params ?? {}, eventId ? { eventID: eventId } : undefined);
};

// Standard Meta events (Lead, Purchase, ViewContent, etc.) — use fbq("track")
// Pass eventId to deduplicate against server-side CAPI events.
// Pass userData (first-party PII captured in this action, e.g. an email the user
// just submitted) to enable Advanced Matching — the pixel hashes it client-side,
// which materially improves Event Match Quality. Only pass real captured data.
export const trackMetaStandardEvent = (
  action: string,
  params?: Record<string, unknown>,
  eventId?: string,
  userData?: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
  },
) => {
  if (typeof window === "undefined") return;
  if (typeof window.fbq !== "function") return;
  if (!metaAllowedHere()) return;

  const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID;
  if (userData && pixelId) {
    const am: Record<string, string> = {};
    if (userData.email) am.em = userData.email.trim().toLowerCase();
    if (userData.phone) am.ph = userData.phone.replace(/[^\d]/g, "");
    if (userData.firstName) am.fn = userData.firstName.trim().toLowerCase();
    if (userData.lastName) am.ln = userData.lastName.trim().toLowerCase();
    // The pixel auto-hashes advanced-matching params; re-init applies them to
    // this and subsequent events (does not fire a PageView on its own).
    if (Object.keys(am).length > 0) window.fbq("init", pixelId, am);
  }

  window.fbq("track", action, params ?? {}, eventId ? { eventID: eventId } : undefined);
};

// ──────────────────────────────────────────────
// Google Ads conversion tracking
// ──────────────────────────────────────────────
// Setup: add NEXT_PUBLIC_GOOGLE_ADS_ID=AW-XXXXXXXXXX to .env
// Per conversion, create action in Google Ads UI → grab "Conversion label" (e.g. "abCdEf12-Ghi")
// Add per-conversion labels to .env, e.g.:
//   NEXT_PUBLIC_GADS_LABEL_QUIZ=AbCdEf12-Ghi
//   NEXT_PUBLIC_GADS_LABEL_ZENYRA=AbCdEf12-Ghi
//   NEXT_PUBLIC_GADS_LABEL_SIGNUP=AbCdEf12-Ghi
//   NEXT_PUBLIC_GADS_LABEL_NEWSLETTER=AbCdEf12-Ghi
//   NEXT_PUBLIC_GADS_LABEL_DEMO=AbCdEf12-Ghi

const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;

type GoogleAdsConversionOptions = {
  /** Conversion label from Google Ads (the part after the `/`) */
  label: string;
  /** Optional conversion value in EUR (default: 50) */
  value?: number;
  /** Currency code (default: EUR) */
  currency?: string;
  /** Unique transaction ID for deduplication (e.g. session id) */
  transactionId?: string;
  /** Hashed user data for Enhanced Conversions (email) */
  userEmail?: string;
};

/**
 * Fire a Google Ads conversion event.
 * No-op if NEXT_PUBLIC_GOOGLE_ADS_ID is not set or gtag is unavailable.
 */
export const trackGoogleAdsConversion = (opts: GoogleAdsConversionOptions) => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  if (!GOOGLE_ADS_ID) return;
  if (!opts.label) return;

  // Enhanced Conversions — send hashed PII for better match rate (iOS, cookieless).
  // gtag handles the hashing client-side when using set('user_data').
  if (opts.userEmail) {
    window.gtag("set", "user_data", {
      email: opts.userEmail.trim().toLowerCase(),
    });
  }

  window.gtag("event", "conversion", {
    send_to: `${GOOGLE_ADS_ID}/${opts.label}`,
    value: opts.value ?? 50,
    currency: opts.currency ?? "EUR",
    ...(opts.transactionId ? { transaction_id: opts.transactionId } : {}),
  });
};

/**
 * Convenience wrapper for common Mindtale conversions.
 * Fires both client-side gtag (Google Ads conversion tag) AND server-side
 * via GA4 Measurement Protocol — recovers conversions when adblockers block gtag.
 *
 * Server-side path reads `_ga` + `_gcl_aw` cookies (set synchronously in layout)
 * and sends a `generate_lead` event via GA4 → Google Ads.
 */
export const trackGoogleAdsLead = (
  kind:
    | "quiz"
    | "zenyra"
    | "signup"
    | "newsletter"
    | "demo"
    | "webinar",
  opts?: Pick<GoogleAdsConversionOptions, "value" | "transactionId" | "userEmail">,
) => {
  // Every conversion is logged as a nominal 1 RON. These are free / low-friction
  // lead actions and we don't use value-based bidding, so a flat token value
  // keeps reporting clean and matches the Google Ads account currency (RON).
  const value = opts?.value ?? 1;
  const currency = "RON";

  // 1) Client-side gtag (works without adblocker)
  const labelMap: Record<string, string | undefined> = {
    quiz: process.env.NEXT_PUBLIC_GADS_LABEL_QUIZ,
    zenyra: process.env.NEXT_PUBLIC_GADS_LABEL_ZENYRA,
    signup: process.env.NEXT_PUBLIC_GADS_LABEL_SIGNUP,
    newsletter: process.env.NEXT_PUBLIC_GADS_LABEL_NEWSLETTER,
    demo: process.env.NEXT_PUBLIC_GADS_LABEL_DEMO,
    webinar: process.env.NEXT_PUBLIC_GADS_LABEL_WEBINAR,
  };
  const label = labelMap[kind];
  if (label) {
    trackGoogleAdsConversion({
      label,
      value,
      currency,
      transactionId: opts?.transactionId,
      userEmail: opts?.userEmail,
    });
  }

  // 2) Server-side via GA4 Measurement Protocol (recovers adblocker users)
  if (typeof window !== "undefined") {
    if (process.env.NODE_ENV !== "production") {
      console.log("[trackGoogleAdsLead] firing server-side conversion", { kind, value });
    }
    void fetch("/api/google-conversion", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        kind,
        value,
        currency,
        userEmail: opts?.userEmail,
        eventId: opts?.transactionId,
        sourceUrl: window.location.href,
      }),
      keepalive: true,
    }).catch(() => {
      // Silent — never break the conversion flow.
    });
  }
};

// ──────────────────────────────────────────────
// The funnel's ad-platform events
// ──────────────────────────────────────────────
// Meta and Google Ads optimise for exactly two things: a new account (Lead) and
// a paid session (Purchase, fired by the backend on payment). Everything
// cheaper — an email for a recommendation, a test result, the
// newsletter — is a signal, not a Lead: when fourteen actions shared the Lead
// name, a Leads campaign optimised for the cheapest of them.

/**
 * An email typed in exchange for something (recommendation, test result,
 * newsletter). Meta gets a custom event it can report on but that no
 * campaign optimises for; GA4 still gets `generate_lead`.
 */
export const trackEmailCaptured = (
  kind: "quiz" | "zenyra" | "newsletter" | "contact",
  opts: { email: string; leadType: string; eventId?: string },
) => {
  trackMetaEvent("EmailCaptured", { lead_type: opts.leadType }, opts.eventId);
  if (kind === "contact") {
    trackEvent("generate_lead", { lead_type: opts.leadType });
    return;
  }
  trackGoogleAdsLead(kind, { userEmail: opts.email });
};

/**
 * GA4 User-ID: the Clerk id, never an email. The backend sends the
 * server-side funnel steps (discovery attended / no-show, purchase) with the
 * same id, so GA4 joins them to this person's browser history. `null` on
 * sign-out stops attributing further events to the previous user.
 */
export const setAnalyticsUserId = (userId: string | null) => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  window.gtag("set", { user_id: userId ?? undefined });
};

const ACCOUNT_LEAD_KEY = "mt_account_lead";

/**
 * The qualified lead: a verified account. Fired once per user, by
 * `AccountLeadTracker`, for every sign-up method (email code and Google).
 *
 * The event id `lead_<clerkUserId>` is the one the backend's server-side Lead
 * uses, so Meta counts the pair once; the same id is the Google Ads
 * transaction id, which de-duplicates a reload.
 */
export const trackAccountLead = (opts: {
  userId: string;
  email?: string;
  method: string;
  onSent?: () => void;
}): boolean => {
  if (typeof window === "undefined") return false;
  try {
    if (window.localStorage.getItem(ACCOUNT_LEAD_KEY) === opts.userId) return false;
  } catch {
    /* storage blocked: fire anyway, the event id de-duplicates on both platforms */
  }

  const eventId = `lead_${opts.userId}`;
  const userData = opts.email ? { email: opts.email } : undefined;
  // Both standard events: CompleteRegistration is Meta's name for a sign-up,
  // Lead is what the running campaigns and their history optimise on. Each
  // has its own id so each de-duplicates against its server-side copy.
  trackMetaStandardEvent(
    "CompleteRegistration",
    { method: opts.method },
    `reg_${opts.userId}`,
    userData,
  );
  trackMetaStandardEvent("Lead", { lead_type: "account" }, eventId, userData);
  const signupLabel = process.env.NEXT_PUBLIC_GADS_LABEL_SIGNUP;
  if (signupLabel) {
    trackGoogleAdsConversion({
      label: signupLabel,
      value: 20,
      currency: "RON",
      transactionId: eventId,
      userEmail: opts.email,
    });
  }
  // The one sign-up event for GA4 and PostHog (their standard name), for every
  // method and surface. It replaces recommendation_gate_signup and
  // signup_completed, which only ever saw the email-code path.
  trackEvent(
    "sign_up",
    {
      method: opts.method,
      signup_page: window.location.pathname,
    },
    { onSent: opts.onSent },
  );

  try {
    window.localStorage.setItem(ACCOUNT_LEAD_KEY, opts.userId);
  } catch {
    /* ignore */
  }
  return true;
};

/**
 * For a sign-up flow that reloads or leaves the page right after the account
 * is created: fire the account events now and resolve once GA4 has sent them
 * (1.5 s at most). GA4 batches hits for a moment, and a page that navigates
 * away immediately took the batch holding sign_up with it; the tracking suite
 * caught it. AccountLeadTracker then sees the stored flag and does not fire
 * again.
 */
export const trackAccountLeadBeforeLeaving = (opts: {
  userId: string;
  email?: string;
  method: string;
}): Promise<void> =>
  new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    window.setTimeout(done, 1500);
    if (!trackAccountLead({ ...opts, onSent: done })) done();
  });
